import moment from 'moment-timezone';
import _ from 'lodash';
import * as uuid from 'uuid';

import * as api from '../../api';
import * as store from '../../reducers/store';
import { parseValue } from 'common/dist/utils/types';
import { convertPlaceToAddress } from '../AddressInput/utils';
import { getValueFromFieldOrPatient, Option } from '../utils';

const { Primitive } = api.PrevisitFormTemplateFieldConfig;
const minDate = new Date(0);
type AnswerDate = { value: string; updated: Date };

export function getLatestAnswerOfPrimitive(
  forms: store.Forms,
  targetPrimitive: api.PrevisitFormTemplateFieldConfig.Primitive,
): string {
  let answerDate: AnswerDate = { value: '', updated: minDate };
  for (const form of Object.values(forms)) {
    for (const { primitive, value, updated } of form.fields) {
      const updatedDate = new Date(updated);
      if (primitive !== targetPrimitive) continue;
      if (updatedDate < answerDate.updated) continue;
      answerDate = { value, updated: updatedDate };
    }
  }

  return answerDate.value;
}

export function getGuarantorsFromInsurancePrimitive(
  forms: store.Forms,
  primitive: api.PrevisitFormTemplateFieldConfig.Primitive = Primitive.PatientInsuranceCardUpload,
): api.Guarantor.PrevisitGuarantor[] {
  const answerValue = getLatestAnswerOfPrimitive(forms, primitive);
  const value =
    parseValue<api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue>(
      answerValue,
    );
  if (!value) return [];

  const { subscribers } = value;

  return subscribers.map((subscriber) => ({
    ehrInformationId: null,
    ehrId: null,
    deletedTimestamp: null,
    kind: null,
    emailAddress: null,
    phoneNumberMobile: null,
    phoneNumberWork: null,
    race: null,
    raceRaw: null,
    ssn4: null,
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
    addressId: subscriber.primaryPolicyHolderAddressId,
    id: subscriber.id,
    firstName: subscriber.primaryPolicyHolderFirstName,
    lastName: subscriber.primaryPolicyHolderLastName,
    relationshipToPatient: subscriber.patientRelationshipToPrimaryPolicyholder,
    relationshipToPatientRaw:
      subscriber.patientRelationshipToPrimaryPolicyholder,
    sex: subscriber.primaryPolicyHolderSex,
    dateOfBirth: moment(subscriber.primaryPolicyHolderDob).toDate(),
    address: subscriber.primaryPolicyHolderAddress,
    phoneNumberHome: subscriber.primaryPolicyHolderHomePhone,
    employmentStatus: subscriber.primaryPolicyHolderEmploymentStatus,
    employmentStatusRaw: subscriber.primaryPolicyHolderEmploymentStatusRaw,
    employer: subscriber.primaryPolicyHolderEmployer,
    retirementDate: moment(
      subscriber.primaryPolicyHolderRetirementDate,
    ).toDate(),
    newlyAdded: subscriber.newlyAdded ?? false,
  }));
}

export function getSubscribersFromGuarantorsPrimitive(
  forms: store.Forms,
): api.PrevisitPatientInsurance.PrevisitSubscriber[] {
  const answerValue = getLatestAnswerOfPrimitive(
    forms,
    Primitive.PatientGuarantors,
  );
  const value =
    parseValue<api.Guarantor.PrevisitGuarantorFormFieldValue>(answerValue);
  if (!value || !value.selectedGuarantorId) return [];

  // get selected guarantor only
  return value.guarantors
    .filter(({ id }) => id === value.selectedGuarantorId)
    .map((guarantor) => ({
      primaryPolicyHolderFirstName: guarantor.firstName,
      primaryPolicyHolderMiddleName: null,
      primaryPolicyHolderLastName: guarantor.lastName,
      primaryPolicyHolderDob: moment(guarantor.dateOfBirth).toDate(),
      primaryPolicyHolderMemberId: null,
      primaryPolicyHolderSex: guarantor.sex,
      primaryPolicyHolderAddressId: guarantor.addressId,
      primaryPolicyHolderHomePhone: guarantor.phoneNumberHome,
      primaryPolicyHolderWorkPhone: guarantor.phoneNumberWork,
      primaryPolicyHolderEmployer: guarantor.employer,
      primaryPolicyHolderRetirementDate: moment(
        guarantor.retirementDate,
      ).toDate(),
      primaryPolicyHolderEmploymentStatus: guarantor.employmentStatus,
      primaryPolicyHolderEmploymentStatusRaw: guarantor.employmentStatusRaw,
      primaryPolicyHolderRelationshipToGuarantor: api.Common.Relationship.Self,
      primaryPolicyHolderRelationshipToGuarantorRaw:
        api.Common.Relationship.Self,
      patientRelationshipToPrimaryPolicyholder: guarantor.relationshipToPatient,
      primaryPolicyHolderAddress: guarantor.address,
      id: guarantor.id,
    }));
}

export function mergeValues<T extends { id: string }>(
  fieldValues: T[],
  formValues: T[],
): T[] {
  const fieldIds = fieldValues.map((v) => v.id);
  const formValuesFiltered = formValues.filter((v) => !fieldIds.includes(v.id));
  return [...fieldValues, ...formValuesFiltered];
}

export interface Named {
  id: string;
  firstName: string | null;
  lastName: string | null;
  relationshipToPatient: api.Common.Relationship | null;
  newlyAdded?: boolean;
}

export function getEnumOptions(args: {
  onlySelf?: boolean;
  patientAge?: number;
  contacts: Named[];
  selfContact?: Named;
}): {
  options: Option<string>[];
  someoneElseId: string;
} {
  const { onlySelf, patientAge, contacts, selfContact } = args;

  const someoneElseId = contacts.find((c) => c.newlyAdded)?.id || uuid.v4();

  const minorNoSelfFilter =
    patientAge !== undefined && patientAge < 18
      ? (contact: Named) =>
          !contact.newlyAdded &&
          contact.relationshipToPatient !== api.Common.Relationship.Self
      : (contact: Named) => !contact.newlyAdded;

  // Remove no-name subscriber
  const noNameFilter = (contact: Named) =>
    !![contact.firstName, contact.lastName].join('');

  const options = onlySelf
    ? [
        ...(selfContact
          ? [
              {
                display: [selfContact.firstName, selfContact.lastName].join(
                  ' ',
                ),
                value: selfContact.id,
                key: selfContact.id,
              },
            ]
          : []),
      ]
    : [
        ...contacts
          .filter(minorNoSelfFilter)
          .filter(noNameFilter)
          .map((contact) => {
            return {
              display: [contact.firstName, contact.lastName].join(' '),
              value: contact.id,
              key: contact.id,
            };
          }),
        {
          display: 'Someone else',
          value: someoneElseId,
          key: someoneElseId,
        },
      ];

  return { options, someoneElseId };
}

export function getPatientDetails(
  forms: store.Forms,
  patient: api.Patient.Patient,
) {
  const fields = _.flatten(Object.values(forms).map((form) => form.fields));
  const patientCellPhoneNumber =
    getValueFromFieldOrPatient(
      api.PrevisitFormTemplateFieldConfig.Primitive.PatientCellPhone,
      fields,
      patient,
    ) || undefined;
  const patientPlaceDetails = parseValue<api.Places.PlaceDetails>(
    getValueFromFieldOrPatient(
      api.PrevisitFormTemplateFieldConfig.Primitive.PatientAddressMailing,
      fields,
      patient,
    ) || '',
  );
  const patientAddressDetails =
    patientPlaceDetails && convertPlaceToAddress(patientPlaceDetails);
  return {
    patientAddressDetails,
    patientCellPhoneNumber,
  };
}
